import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import html2canvas from 'html2canvas';
import { Maximize2, Minimize2, X } from 'lucide-react';

interface RIRMeasurement {
  id: string;
  label: string;
  length?: string;
  height?: string;
  insulation?: string;
  insulationType?: string;
  visible?: boolean;
  value: string;
  unit: string;
}

interface RIRAnnotation {
  id: string;
  x: number;
  y: number;
  text: string;
}

interface RoomInRoofDiagramProps {
  onDataChange?: (data: {
    measurements: RIRMeasurement[];
    annotations: RIRAnnotation[];
    insulationType?: string;
    ventilationType?: string;
    diagramType?: 'standard' | 'with-dormer';
    diagramImage?: string;
  }) => void;
  initialMeasurements?: RIRMeasurement[];
  initialAnnotations?: RIRAnnotation[];
  initialInsulationType?: string;
  initialVentilationType?: string;
  initialDiagramType?: 'standard' | 'with-dormer';
  initialDiagramImage?: string;
}

const RoomInRoofDiagram: React.FC<RoomInRoofDiagramProps> = ({
  onDataChange,
  initialMeasurements = [],
  initialAnnotations = [],
  initialInsulationType = 'between-rafters',
  initialVentilationType = 'ridge-and-eaves',
  initialDiagramType = 'standard',
  initialDiagramImage = ''
}) => {
  const [measurements, setMeasurements] = useState<RIRMeasurement[]>(initialMeasurements);
  const [annotations, setAnnotations] = useState<RIRAnnotation[]>(initialAnnotations);
  const [insulationType, setInsulationType] = useState(initialInsulationType);
  const [ventilationType, setVentilationType] = useState(initialVentilationType);
  const [diagramType, setDiagramType] = useState<'standard' | 'with-dormer'>(initialDiagramType);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const diagramRef = useRef<HTMLDivElement>(null);
  const [diagramImage, setDiagramImage] = useState<string>(initialDiagramImage);
  const hasInitializedFromProps = useRef(false);
  const [isSvgImageLoaded, setIsSvgImageLoaded] = useState(false);
  const svgImageRef = useRef<HTMLImageElement>(null);

  // Debug: Log component mount
  useEffect(() => {
    console.log('🎨 RIR DIAGRAM: Component mounted', {
      diagramType,
      hasInitialDiagramImage: !!initialDiagramImage,
      initialImageLength: initialDiagramImage?.length
    });
    return () => console.log('🎨 RIR DIAGRAM: Component unmounting');
  }, []);

  // Update state when props are loaded from database (only once per mount for measurements/image)
  useEffect(() => {
    // Check if we have data in props and haven't initialized yet
    const hasData = initialMeasurements.length > 0 || initialDiagramImage;
    
    if (hasData && !hasInitializedFromProps.current) {
      if (initialMeasurements.length > 0) {
        setMeasurements(initialMeasurements);
      }
      if (initialAnnotations.length > 0) {
        setAnnotations(initialAnnotations);
      }
      if (initialInsulationType) {
        setInsulationType(initialInsulationType);
      }
      if (initialVentilationType) {
        setVentilationType(initialVentilationType);
      }
      if (initialDiagramImage) {
        setDiagramImage(initialDiagramImage);
      }
      
      hasInitializedFromProps.current = true;
    }
  }, [initialMeasurements, initialAnnotations, initialInsulationType, initialVentilationType, initialDiagramImage]);
  
  // Always sync diagramType with props (for both hidden and visible instances)
  useEffect(() => {
    if (initialDiagramType && initialDiagramType !== diagramType) {
      console.log('🔄 RIR DIAGRAM: Syncing diagramType from props', {
        from: diagramType,
        to: initialDiagramType
      });
      setDiagramType(initialDiagramType);
    }
  }, [initialDiagramType]);
  
  // Separate effect to reset initialization flag on unmount only
  useEffect(() => {
    return () => {
      hasInitializedFromProps.current = false;
    };
  }, []); // Empty deps - only runs on mount/unmount

  // Manage body scroll when fullscreen overlay is active
  useEffect(() => {
    if (!isFullscreen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isFullscreen]);

  // Capture diagram as image
  const captureDiagramImage = useCallback(async () => {
    if (!diagramRef.current) {
      console.error('⚠️ RIR CAPTURE: diagramRef not ready');
      return null;
    }
    
    try {
      console.log('📸 RIR CAPTURE: Starting...', {
        hasRef: !!diagramRef.current,
        refVisible: diagramRef.current.offsetHeight > 0,
        refDimensions: `${diagramRef.current.offsetWidth}x${diagramRef.current.offsetHeight}`
      });
      
      // html2canvas cannot capture SVG <img> elements, so we need to convert SVG to canvas first
      const svgImg = diagramRef.current.querySelector('img') as HTMLImageElement;
      if (!svgImg) {
        console.error('❌ RIR CAPTURE: No SVG image found in diagram');
        return null;
      }
      
      // Check if image is loaded
      if (!svgImg.complete || !svgImg.naturalWidth) {
        console.error('❌ RIR CAPTURE: SVG image not fully loaded', {
          complete: svgImg.complete,
          naturalWidth: svgImg.naturalWidth,
          src: svgImg.src?.substring(0, 100)
        });
        return null;
      }
      
      // Wait for SVG to load
      if (!svgImg.complete) {
        await new Promise((resolve) => {
          svgImg.onload = resolve;
          svgImg.onerror = resolve;
          setTimeout(resolve, 3000);
        });
      }
      
      // Fetch the SVG content
      const svgUrl = svgImg.src;
      const response = await fetch(svgUrl);
      const svgText = await response.text();
      
      // Parse SVG to get dimensions
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
      const svgElement = svgDoc.documentElement;
      
      // Get SVG dimensions
      const width = parseInt(svgElement.getAttribute('width') || '1024');
      const height = parseInt(svgElement.getAttribute('height') || '1024');
      
      // Create a canvas and draw the SVG
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = width;
      tempCanvas.height = height;
      const ctx = tempCanvas.getContext('2d');
      
      if (!ctx) {
        throw new Error('Failed to get canvas context');
      }
      
      // Create an image from the SVG text
      const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
      const svgObjectUrl = URL.createObjectURL(svgBlob);
      
      const img = new Image();
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = svgObjectUrl;
      });
      
      // Draw SVG to canvas
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      
      // Clean up
      URL.revokeObjectURL(svgObjectUrl);
      
      // Now replace the img with the canvas temporarily for html2canvas
      const parent = svgImg.parentElement;
      if (!parent) {
        throw new Error('SVG image has no parent');
      }
      
      tempCanvas.className = svgImg.className;
      tempCanvas.style.cssText = svgImg.style.cssText;
      parent.replaceChild(tempCanvas, svgImg);
      
      // Capture the full diagram with html2canvas (now includes the canvas-rendered SVG)
      const finalCanvas = await html2canvas(diagramRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true
      });
      
      // Restore the original img element
      parent.replaceChild(svgImg, tempCanvas);
      
      const dataUrl = finalCanvas.toDataURL('image/png');
      
      // Validate the captured data URL
      if (!dataUrl || dataUrl === 'data:,' || dataUrl.length < 100) {
        console.error('❌ RIR CAPTURE: Invalid data URL generated', {
          length: dataUrl?.length,
          preview: dataUrl?.substring(0, 50)
        });
        return null;
      }
      
      console.log('✅ RIR CAPTURE: Complete', { 
        size: `${Math.round(dataUrl.length / 1024)}KB`,
        preview: dataUrl.substring(0, 50)
      });
      
      setDiagramImage(dataUrl);
      
      // Immediately notify parent of the new diagram (don't wait for useEffect)
      if (onDataChange) {
        onDataChange({
          measurements,
          annotations,
          insulationType,
          ventilationType,
          diagramType,
          diagramImage: dataUrl // Use the new dataUrl directly
        });
      }
      
      return dataUrl;
    } catch (error) {
      console.error('❌ RIR CAPTURE: Failed to capture diagram image:', error);
      return null;
    }
  }, [measurements, annotations, insulationType, ventilationType, diagramType, onDataChange]);

  // Capture diagram when SVG image finishes loading
  useEffect(() => {
    if (isSvgImageLoaded) {
      console.log('🎯 RIR: SVG loaded, capturing diagram');
      // Small delay to ensure rendering is complete
      const timer = setTimeout(() => captureDiagramImage(), 300);
      return () => clearTimeout(timer);
    }
  }, [isSvgImageLoaded, captureDiagramImage]);

  // Reset loaded state when diagram type changes
  useEffect(() => {
    console.log('🔄 RIR: Diagram type changed, resetting loaded state');
    setIsSvgImageLoaded(false);
  }, [diagramType]);

  // Capture diagram when data changes (with delay to batch rapid changes)
  useEffect(() => {
    if (!isSvgImageLoaded) return; // Only capture when SVG is loaded
    
    const timer = setTimeout(() => {
      console.log('🔄 RIR: Auto-capturing diagram after data change');
      captureDiagramImage();
    }, 800);
    return () => clearTimeout(timer);
  }, [measurements, annotations, insulationType, ventilationType, captureDiagramImage, isSvgImageLoaded]);

  // Notify parent of changes (including diagram image) - via useEffect for regular updates with throttling
  useEffect(() => {
    // Skip notification if still initializing from props
    if (!hasInitializedFromProps.current) return;
    
    // Warn about invalid diagram images but still notify parent to persist other changes
    if (diagramImage && (diagramImage === 'data:,' || diagramImage.length < 100)) {
      console.warn('⚠️ RIR: Invalid diagram image but notifying parent anyway', {
        length: diagramImage.length,
        diagramType
      });
    }
    
    if (onDataChange) {
      // Throttle parent notifications to prevent excessive updates during rapid typing
      const notifyTimer = setTimeout(() => {
        onDataChange({
          measurements,
          annotations,
          insulationType,
          ventilationType,
          diagramType,
          diagramImage
        });
      }, 300); // 300ms throttle for parent notifications
      
      return () => clearTimeout(notifyTimer);
    }
  }, [measurements, annotations, insulationType, ventilationType, diagramType, diagramImage, onDataChange]);

  const handleMeasurementChange = (id: string, field: 'value' | 'label', value: string) => {
    setMeasurements(prev =>
      prev.map(m => (m.id === id ? { ...m, [field]: value } : m))
    );
  };


  // Render the room in roof diagram using the provided SVG
  const renderRIRDiagram = () => {
    const diagramSrc = diagramType === 'with-dormer' 
      ? "/examples/rir diagram with dormer.svg"
      : "/examples/rir diagram.svg";
    
    console.log('📊 RIR: Rendering diagram', {
      diagramType,
      diagramSrc,
      isSvgImageLoaded
    });
    
    // Standard diagram labels
    const standardLabels = [
      { text: 'Party Wall Area', x: 485.8511627906977, y: 258.9395348837209, lineToX: 616.6790697674419, lineToY: 346.74418604651163 },
      { text: 'Flat Ceiling Area', x: 762.0093023255814, y: 320.4651162790698, lineToX: 579.2744186046511, lineToY: 397.9720930232558 },
      { text: 'Sloping Ceiling Area 1', x: 808.9767441860465, y: 383.6, lineToX: 661.5255813953488, lineToY: 421.7953488372093 },
      { text: 'Stud Wall Area 1', x: 901.693023255814, y: 468.0093023255814, lineToX: 638.5023255813953, lineToY: 497.5813953488372 },
      { text: 'Residual Area 1', x: 708.9860465116279, y: 577.1255813953488, lineToX: 632.1209302325582, lineToY: 545.7302325581395 },
      { text: 'Sloping Ceiling Area 2', x: 201.6, y: 355.7767441860465, lineToX: 321.86976744186046, lineToY: 516.9302325581396 },
      { text: 'Stud Wall Area 2', x: 120, y: 510, lineToX: 280.5581395348837, lineToY: 540.4 },
      { text: 'Residual Area 2', x: 120, y: 570, lineToX: 264.6418604651163, lineToY: 614.4186046511628 },
      { text: 'RIR Floor Area', x: 380, y: 680, lineToX: 415.6744186046512, lineToY: 629.4604651162791 },
      { text: 'Stud Wall 2', x: 200, y: 750, lineToX: 303.7860465116279, lineToY: 591.6837209302325 },
      { text: 'Stud Wall 1', x: 600, y: 750, lineToX: 492.89302325581394, lineToY: 600.353488372093 },
      { text: 'Gable Wall Area', x: 484.0558139534884, y: 778.7720930232558, lineToX: 425.8139534883721, lineToY: 576.046511627907 },
    ];

    // Dormer diagram labels
    const dormerLabels = [
      { text: 'Party Wall Area', x: 320, y: 320, lineToX: 418.4372093023256, lineToY: 479.27441860465115 },
      { text: 'Flat Ceiling Area', x: 528.5674418604651, y: 407.69302325581396, lineToX: 319.50697674418603, lineToY: 576.4651162790698 },
      { text: 'Sloping Ceiling Area 1', x: 514, y: 446.0186046511628, lineToX: 453.51627906976745, lineToY: 555.6279069767442 },
      { text: 'Stud Wall Area 1', x: 536.5581395348837, y: 515.4325581395349, lineToX: 487.86046511627904, lineToY: 600.5488372093023 },
      { text: 'Residual Area 1', x: 573.9162790697675, y: 586.2976744186046, lineToX: 474.3720930232558, lineToY: 654.3255813953489 },
      { text: 'Sloping Ceiling Area 2', x: 167.5906976744186, y: 389.3860465116279, lineToX: 163.62790697674419, lineToY: 629.5441860465116 },
      { text: 'Stud Wall Area 2', x: 120, y: 510, lineToX: 139.6, lineToY: 654.0930232558139 },
      { text: 'Residual Area 2', x: 120, y: 570, lineToX: 81.45116279069768, lineToY: 743.9255813953488 },
      { text: 'RIR Floor Area', x: 525.0883720930233, y: 856.0837209302325, lineToX: 230.44651162790697, lineToY: 722.8651162790698 },
      { text: 'Stud Wall 2', x: 87.61860465116278, y: 833.4697674418604, lineToX: 111.12558139534883, lineToY: 756.2046511627907 },
      { text: 'Stud Wall 1', x: 514.5767441860465, y: 730.1023255813953, lineToX: 301.36744186046514, lineToY: 730.0093023255814 },
      { text: 'Gable Wall Area', x: 519.7581395348838, y: 924.9116279069767, lineToX: 165.69302325581396, lineToY: 736.5674418604651 },
      { text: 'Cheek Area 1', x: 547.9348837209302, y: 300.3255813953488, lineToX: 734.7255813953489, lineToY: 361.10697674418606 },
      { text: 'Cheek Area 2', x: 896.7441860465116, y: 277.0046511627907, lineToX: 835.953488372093, lineToY: 317.86976744186046 },
      { text: 'Dormer Ceiling Area', x: 823.2558139534884, y: 454.1674418604651, lineToX: 788.4, lineToY: 331.5906976744186 },
    ];
    
    // Use default labels based on diagram type and filter by visibility
    const allLabels = diagramType === 'with-dormer' ? dormerLabels : standardLabels;
    const labels = allLabels.filter(label => {
      const measurement = measurements.find(m => m.label === label.text);
      return measurement ? measurement.visible !== false : true; // Show by default if not found
    });
    
    return (
      <div 
        ref={diagramRef}
        className="relative w-full"
      >
        {/* Base SVG diagram from file */}
        <img 
          ref={svgImageRef}
          src={diagramSrc}
          alt={`Room in Roof Diagram${diagramType === 'with-dormer' ? ' with Dormer' : ''}`}
          className="w-full h-auto"
          style={{ display: 'block', objectFit: 'contain' }}
          draggable={false}
          crossOrigin="anonymous"
          onLoad={() => {
            console.log('✅ RIR: SVG image loaded successfully');
            setIsSvgImageLoaded(true);
          }}
          onError={(e) => {
            console.error('❌ RIR: SVG image failed to load', e);
          }}
        />
        
        {/* Overlay for labels, arrows, and annotations */}
        <svg
          className="absolute top-0 left-0 w-full h-full pointer-events-none"
          viewBox="0 0 1024 1024"
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            {/* Arrow marker */}
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="10"
              refX="9"
              refY="3"
              orient="auto"
            >
              <polygon points="0 0, 10 3, 0 6" fill="#1F2937" />
            </marker>
          </defs>
          
          {/* Area labels with arrows */}
          {labels.map((label, index) => (
            <g key={`label-${index}`}>
              {/* Arrow line */}
              <line
                x1={label.x}
                y1={label.y}
                x2={label.lineToX}
                y2={label.lineToY}
                stroke="#1F2937"
                strokeWidth="2"
                markerEnd="url(#arrowhead)"
              />
              
              {/* Label text */}
              <text
                x={label.x}
                y={label.y - 5}
                fontSize="14"
                fill="#1F2937"
                fontWeight="600"
                textAnchor={label.x > 512 ? 'start' : 'end'}
              >
                {label.text}
              </text>
            </g>
          ))}
          
          {/* User annotations (kept for backwards compatibility with saved data) */}
          {annotations.map(annotation => (
            <g key={annotation.id}>
              <circle
                cx={annotation.x}
                cy={annotation.y}
                r="8"
                fill="#EF4444"
                stroke="white"
                strokeWidth="3"
              />
              <text
                x={annotation.x + 15}
                y={annotation.y + 6}
                fontSize="18"
                fill="#1F2937"
                fontWeight="700"
                stroke="white"
                strokeWidth="0.5"
                paintOrder="stroke"
              >
                {annotation.text}
              </text>
            </g>
          ))}
        </svg>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Mobile Warning Banner */}
      <div className="p-3 bg-amber-50 border-l-4 border-amber-500 rounded-lg lg:hidden">
        <div className="flex items-start gap-2">
          <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div>
            <h4 className="text-sm font-semibold text-amber-800 mb-1">Mobile Device Detected</h4>
            <p className="text-xs text-amber-700">For the best experience on phones, tap the Fullscreen diagram button below. You can still edit all measurements and details without needing to scroll sideways.</p>
          </div>
        </div>
      </div>
      
      {/* Mobile fullscreen toggle */}
      <div className="flex justify-end mb-4 lg:hidden">
        <button
          type="button"
          onClick={() => setIsFullscreen(true)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200"
        >
          <Maximize2 className="w-3 h-3" />
          <span>Fullscreen diagram</span>
        </button>
      </div>
      
      {/* Diagram Type Selector */}
      <div className="bg-white p-3 sm:p-6 rounded-xl border border-slate-200 shadow-lg">
        <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-3 sm:mb-4">Room Configuration</h3>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => setDiagramType('standard')}
            className={`px-3 py-2 sm:px-4 sm:py-3 rounded-lg border-2 transition-all text-left ${
              diagramType === 'standard'
                ? 'border-blue-500 bg-blue-50'
                : 'border-slate-300 bg-white hover:border-slate-400'
            }`}
          >
            <div className="flex items-center gap-2">
              <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                diagramType === 'standard' 
                  ? 'border-blue-500 bg-blue-500' 
                  : 'border-slate-300'
              }`}>
                {diagramType === 'standard' && (
                  <div className="w-full h-full rounded-full bg-white scale-50"></div>
                )}
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 text-sm">Standard Room in Roof</div>
                <div className="text-xs text-slate-600 mt-0.5 hidden sm:block">Standard loft conversion without dormer</div>
              </div>
            </div>
          </button>
          
          <button
            type="button"
            onClick={() => setDiagramType('with-dormer')}
            className={`px-3 py-2 sm:px-4 sm:py-3 rounded-lg border-2 transition-all text-left ${
              diagramType === 'with-dormer'
                ? 'border-blue-500 bg-blue-50'
                : 'border-slate-300 bg-white hover:border-slate-400'
            }`}
          >
            <div className="flex items-center gap-2">
              <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                diagramType === 'with-dormer' 
                  ? 'border-blue-500 bg-blue-500' 
                  : 'border-slate-300'
              }`}>
                {diagramType === 'with-dormer' && (
                  <div className="w-full h-full rounded-full bg-white scale-50"></div>
                )}
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 text-sm">Room in Roof with Dormer</div>
                <div className="text-xs text-slate-600 mt-0.5 hidden sm:block">Loft conversion with dormer window</div>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Diagram (hidden on small devices below 700px, available via fullscreen) */}
      <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-4 sm:p-8 rounded-xl border border-slate-200 shadow-lg overflow-x-auto hidden lg:block">
        <div className="max-w-full mx-auto min-w-[600px] sm:min-w-0" style={{ minHeight: '600px' }}>
          {renderRIRDiagram()}
        </div>
        
        {/* Diagram Info */}
        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Use the table below to add notes for each labeled area on the diagram.
          </p>
        </div>
      </div>

      {/* Area Details - Grid Layout */}
      <div className="bg-white p-3 sm:p-6 rounded-xl border border-slate-200 shadow-lg">
        <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-3 sm:mb-4">Area Details</h3>
        
        <div className="space-y-0">
          {(diagramType === 'with-dormer' ? [
            'Party Wall Area',
            'Flat Ceiling Area',
            'Sloping Ceiling Area 1',
            'Stud Wall Area 1',
            'Residual Area 1',
            'Sloping Ceiling Area 2',
            'Stud Wall Area 2',
            'Residual Area 2',
            'RIR Floor Area',
            'Stud Wall 2',
            'Stud Wall 1',
            'Gable Wall Area',
            'Cheek Area 1',
            'Cheek Area 2',
            'Dormer Ceiling Area'
          ] : [
            'Party Wall Area',
            'Flat Ceiling Area',
            'Sloping Ceiling Area 1',
            'Stud Wall Area 1',
            'Residual Area 1',
            'Sloping Ceiling Area 2',
            'Stud Wall Area 2',
            'Residual Area 2',
            'RIR Floor Area',
            'Stud Wall 2',
            'Stud Wall 1',
            'Gable Wall Area'
          ]).map((areaName) => {
            const measurement = measurements.find(m => m.label === areaName) || {
              id: areaName,
              label: areaName,
              length: '',
              height: '',
              insulation: '',
              insulationType: '',
              visible: true,
              value: '',
              unit: ''
            };
            
            const updateField = (field: 'length' | 'height' | 'insulation' | 'insulationType', value: string) => {
              const existing = measurements.find(m => m.label === areaName);
              if (existing) {
                setMeasurements(prev =>
                  prev.map(m => (m.label === areaName ? { ...m, [field]: value } : m))
                );
              } else {
                setMeasurements(prev => [...prev, {
                  id: Date.now().toString(),
                  label: areaName,
                  length: field === 'length' ? value : '',
                  height: field === 'height' ? value : '',
                  insulation: field === 'insulation' ? value : '',
                  insulationType: field === 'insulationType' ? value : '',
                  visible: true,
                  value: '',
                  unit: ''
                }]);
              }
            };
            
            const toggleVisibility = () => {
              const existing = measurements.find(m => m.label === areaName);
              if (existing) {
                setMeasurements(prev =>
                  prev.map(m => (m.label === areaName ? { ...m, visible: !m.visible } : m))
                );
              } else {
                setMeasurements(prev => [...prev, {
                  id: Date.now().toString(),
                  label: areaName,
                  length: '',
                  height: '',
                  insulation: '',
                  insulationType: '',
                  visible: false,
                  value: '',
                  unit: ''
                }]);
              }
            };
            
            return (
              <div key={areaName} className="py-2 border-b border-slate-200/60">
                {/* Grid layout like wall insulation section */}
                <div className="grid grid-cols-1 gap-2">
                  {/* Row 1: Checkbox + Area Name */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={measurement.visible !== false}
                      onChange={toggleVisibility}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500 cursor-pointer flex-shrink-0"
                    />
                    <span className="text-sm font-medium text-slate-700">{areaName}</span>
                  </div>
                  
                  {/* Row 2: Length + Height (stacked on mobile) */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-slate-600 block mb-1">Length (m)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={measurement.length || ''}
                        onChange={(e) => updateField('length', e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600 block mb-1">Height (m)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={measurement.height || ''}
                        onChange={(e) => updateField('height', e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  
                  {/* Row 3: Insulation + Type (stacked on mobile) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-slate-600 block mb-1">Insulation</label>
                      <select
                        value={measurement.insulation || ''}
                        onChange={(e) => updateField('insulation', e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select...</option>
                        <option value="Unknown">Unknown</option>
                        <option value="As Built">As Built</option>
                        <option value="None">None</option>
                        <option value="12 mm">12 mm</option>
                        <option value="25 mm">25 mm</option>
                        <option value="50 mm">50 mm</option>
                        <option value="75 mm">75 mm</option>
                        <option value="100 mm">100 mm</option>
                        <option value="125 mm">125 mm</option>
                        <option value="150 mm">150 mm</option>
                        <option value="175 mm">175 mm</option>
                        <option value="200 mm">200 mm</option>
                        <option value="225 mm">225 mm</option>
                        <option value="250 mm">250 mm</option>
                        <option value="270 mm">270 mm</option>
                        <option value="300 mm">300 mm</option>
                        <option value="350 mm">350 mm</option>
                        <option value="400+ mm">400+ mm</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-600 block mb-1">Type</label>
                      <select
                        value={measurement.insulationType || ''}
                        onChange={(e) => updateField('insulationType', e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select...</option>
                        <option value="Mineral or EPS">Mineral or EPS</option>
                        <option value="PUR or PIR">PUR or PIR</option>
                        <option value="Unknown">Unknown</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Fullscreen Mode Portal (mobile-focused) */}
      {isFullscreen && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] bg-slate-900/95 flex items-center justify-center p-4">
          <div className="relative w-full max-w-[900px] h-full max-h-[90vh] bg-white rounded-xl shadow-2xl flex flex-col">
            {/* Header with close button */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800">
                Room in Roof - {diagramType === 'with-dormer' ? 'With Dormer' : 'Standard'}
              </h3>
              <button
                type="button"
                onClick={() => setIsFullscreen(false)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-slate-100 bg-slate-800 hover:bg-slate-700 border border-slate-600"
              >
                <Minimize2 className="w-3 h-3" />
                <span>Exit fullscreen</span>
              </button>
            </div>

            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Diagram */}
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-4 rounded-lg border border-slate-200">
                <div className="max-w-full mx-auto" style={{ minHeight: '400px' }}>
                  {renderRIRDiagram()}
                </div>
              </div>

              {/* Area Details Table */}
              <div className="bg-white rounded-lg border border-slate-200">
                <h4 className="text-base font-semibold text-slate-800 p-3 border-b border-slate-200">Area Details</h4>
                <div className="overflow-x-auto p-3">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b-2 border-slate-300">
                        <th className="text-center py-2 px-1 font-semibold text-slate-700 w-8">✓</th>
                        <th className="text-left py-2 px-1 font-semibold text-slate-700 whitespace-nowrap">Area</th>
                        <th className="text-left py-2 px-1 font-semibold text-slate-700 whitespace-nowrap">Length (m)</th>
                        <th className="text-left py-2 px-1 font-semibold text-slate-700 whitespace-nowrap">Height (m)</th>
                        <th className="text-left py-2 px-1 font-semibold text-slate-700 whitespace-nowrap">Insulation</th>
                        <th className="text-left py-2 px-1 font-semibold text-slate-700 whitespace-nowrap">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(diagramType === 'with-dormer' ? [
                        'Party Wall Area', 'Flat Ceiling Area', 'Sloping Ceiling Area 1', 'Stud Wall Area 1',
                        'Residual Area 1', 'Sloping Ceiling Area 2', 'Stud Wall Area 2', 'Residual Area 2',
                        'RIR Floor Area', 'Stud Wall 2', 'Stud Wall 1', 'Gable Wall Area',
                        'Cheek Area 1', 'Cheek Area 2', 'Dormer Ceiling Area'
                      ] : [
                        'Party Wall Area', 'Flat Ceiling Area', 'Sloping Ceiling Area 1', 'Stud Wall Area 1',
                        'Residual Area 1', 'Sloping Ceiling Area 2', 'Stud Wall Area 2', 'Residual Area 2',
                        'RIR Floor Area', 'Stud Wall 2', 'Stud Wall 1', 'Gable Wall Area'
                      ]).map((areaName) => {
                        const measurement = measurements.find(m => m.label === areaName) || {
                          id: areaName, label: areaName, length: '', height: '', insulation: '',
                          insulationType: '', visible: true, value: '', unit: ''
                        };
                        
                        const updateField = (field: 'length' | 'height' | 'insulation' | 'insulationType', value: string) => {
                          const existing = measurements.find(m => m.label === areaName);
                          if (existing) {
                            setMeasurements(prev => prev.map(m => (m.label === areaName ? { ...m, [field]: value } : m)));
                          } else {
                            setMeasurements(prev => [...prev, {
                              id: Date.now().toString(), label: areaName, length: field === 'length' ? value : '',
                              height: field === 'height' ? value : '', insulation: field === 'insulation' ? value : '',
                              insulationType: field === 'insulationType' ? value : '', visible: true, value: '', unit: ''
                            }]);
                          }
                        };
                        
                        const toggleVisibility = () => {
                          const existing = measurements.find(m => m.label === areaName);
                          if (existing) {
                            setMeasurements(prev => prev.map(m => (m.label === areaName ? { ...m, visible: !m.visible } : m)));
                          } else {
                            setMeasurements(prev => [...prev, {
                              id: Date.now().toString(), label: areaName, length: '', height: '',
                              insulation: '', insulationType: '', visible: false, value: '', unit: ''
                            }]);
                          }
                        };
                        
                        return (
                          <tr key={areaName} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-1 px-1 text-center">
                              <input type="checkbox" checked={measurement.visible !== false} onChange={toggleVisibility}
                                className="w-3 h-3 text-blue-600 border-slate-300 rounded focus:ring-blue-500 cursor-pointer" />
                            </td>
                            <td className="py-1 px-1 font-medium text-slate-700 whitespace-nowrap text-[10px]">{areaName}</td>
                            <td className="py-1 px-1">
                              <input type="number" step="0.01" value={measurement.length || ''} onChange={(e) => updateField('length', e.target.value)}
                                className="w-16 px-1 py-0.5 border border-slate-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-[10px]" placeholder="0.00" />
                            </td>
                            <td className="py-1 px-1">
                              <input type="number" step="0.01" value={measurement.height || ''} onChange={(e) => updateField('height', e.target.value)}
                                className="w-16 px-1 py-0.5 border border-slate-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-[10px]" placeholder="0.00" />
                            </td>
                            <td className="py-1 px-1">
                              <select value={measurement.insulation || ''} onChange={(e) => updateField('insulation', e.target.value)}
                                className="w-20 px-1 py-0.5 border border-slate-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-[10px]">
                                <option value="">Select...</option>
                                <option value="Unknown">Unknown</option>
                                <option value="As Built">As Built</option>
                                <option value="None">None</option>
                                <option value="12 mm">12 mm</option>
                                <option value="25 mm">25 mm</option>
                                <option value="50 mm">50 mm</option>
                                <option value="75 mm">75 mm</option>
                                <option value="100 mm">100 mm</option>
                                <option value="125 mm">125 mm</option>
                                <option value="150 mm">150 mm</option>
                                <option value="175 mm">175 mm</option>
                                <option value="200 mm">200 mm</option>
                                <option value="225 mm">225 mm</option>
                                <option value="250 mm">250 mm</option>
                                <option value="270 mm">270 mm</option>
                                <option value="300 mm">300 mm</option>
                                <option value="350 mm">350 mm</option>
                                <option value="400+ mm">400+ mm</option>
                              </select>
                            </td>
                            <td className="py-1 px-1">
                              <select value={measurement.insulationType || ''} onChange={(e) => updateField('insulationType', e.target.value)}
                                className="w-24 px-1 py-0.5 border border-slate-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-[10px]">
                                <option value="">Select...</option>
                                <option value="Mineral or EPS">Mineral or EPS</option>
                                <option value="PUR or PIR">PUR or PIR</option>
                                <option value="Unknown">Unknown</option>
                              </select>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default RoomInRoofDiagram;
